import { Tree } from "frontend/src/components/Tree/Tree";
import { useQuery } from "@apollo/client";
import CircularProgress from "@mui/material/CircularProgress";

import * as types from "_graphql-types/graphql";
import i18n from "i18next";
import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  removeFilter,
  updateFilter,
} from "Reducers/globalSearchV2/globalSearchV2.actions";
import { getInvestmentFilters } from "Reducers/globalSearchV2/globalSearchV2.selectors";
import type { AntdTreeNode } from "frontend/src/components/Tree/treeData";
import { FilterInterface } from "../FilterInterface";
import { FETCH_TYPE_ASSET_CLASSES_STRATEGIES } from "../graphql";
import { useDispatch } from "src/react-redux";

function TypeFilterInput({
  filter,
  setFilter,
  availableTypeAssetClassStrategies,
}: {
  filter: types.EntitySearchFilter;
  setFilter: React.Dispatch<React.SetStateAction<types.EntitySearchFilter>>;
  availableTypeAssetClassStrategies: {
    vehicleType: { name: string; id: number };
  }[];
}): JSX.Element {
  const { treeData, optionKey } = useMemo<{
    treeData: AntdTreeNode[];
    optionKey: { [key: string]: string };
  }>(() => {
    const optionKeyMap: { [key: string]: string } = {};

    availableTypeAssetClassStrategies.forEach(option => {
      optionKeyMap[option.vehicleType.id] = option.vehicleType.name;
    });

    return {
      treeData: Object.entries(optionKeyMap).map(([typeId, typeName]) => ({
        key: typeId,
        title: typeName,
      })),
      optionKey: optionKeyMap,
    };
  }, [availableTypeAssetClassStrategies]);

  const setFilterFromKeys = (
    checkedKeys: { checked: React.Key[] } | React.Key[]
  ) => {
    if (!Array.isArray(checkedKeys))
      throw new Error("Unexpected type in type filter.. Antd!");

    setFilter({
      values: checkedKeys.map(key => ({
        id: Number(key),
        label: optionKey[String(key)],
      })),
    });
  };

  const checkedKeysFromFilter = useMemo(
    () => filter.values.map(({ id }) => String(id)),
    [filter]
  );

  return (
    <Tree
      checkable
      treeData={treeData}
      onCheck={setFilterFromKeys}
      checkedKeys={checkedKeysFromFilter}
    />
  );
}

const defaultFilter: types.EntitySearchFilter = { values: [] };

function TypeFilter(): JSX.Element {
  const investmentFilters = useSelector(getInvestmentFilters);

  const typeFilter: types.EntitySearchFilter | null = useMemo(() => {
    const foundFilter = investmentFilters.find(
      filterI => filterI.type === "investment" && filterI.data.type === "TYPE"
    );
    if (!foundFilter) return null;
    return foundFilter.data.data as types.EntitySearchFilter;
  }, [investmentFilters]);

  const { loading, data, error } =
    useQuery<types.GetAssetClassStrategiesForFilterQuery>(
      FETCH_TYPE_ASSET_CLASSES_STRATEGIES
    );

  const [localFilter, setLocalFilter] = useState<types.EntitySearchFilter>(
    typeFilter ?? defaultFilter
  );

  const dispatch = useDispatch();

  useEffect(() => {
    setLocalFilter(typeFilter ?? defaultFilter);
  }, [typeFilter]);

  const setFilter = () => {
    if (localFilter) {
      dispatch(
        updateFilter({
          type: "investment",
          data: {
            type: "investment",
            data: {
              type: "TYPE",
              data: localFilter,
            },
          },
        })
      );
    }
  };

  const reset = () => {
    setLocalFilter(defaultFilter);
    dispatch(
      removeFilter({
        type: "investment",
        data: {
          type: "investment",
          data: {
            type: "TYPE",
            data: defaultFilter,
          },
        },
      })
    );
  };

  return (
    <FilterInterface
      label={i18n.t("search_discovery.filters.labels.type")}
      count={localFilter.values.length}
      reset={reset}
      set={setFilter}
      menuClass="filters__menu-dropdown"
    >
      <div className="main-dropdown__menu-body">
        {loading && <CircularProgress size={20} />}
        {error && <span>Types failed to load</span>}
        {data && (
          <TypeFilterInput
            availableTypeAssetClassStrategies={
              data.typeAssetClassStrategyOptionsList.items
            }
            filter={localFilter}
            setFilter={setLocalFilter}
          />
        )}
      </div>
    </FilterInterface>
  );
}

export default TypeFilter;
